// =============================================================================
// AGC: DROID FARM - Health Score Widget
// AGC: Displays field health score with circular indicator and grade
// =============================================================================

import React from 'react'
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Leaf } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { FieldHealthResult, HealthZones, TrendInfo } from '../../../types'

interface HealthScoreWidgetProps {
  health: FieldHealthResult | null
  trend?: TrendInfo | null
  lastUpdated?: string | null
  isLoading?: boolean
}

/**
 * AGC: Get color for health score
 */
function getScoreColor(score: number): string {
  if (score >= 80) return '#1e6e1e' // AGC: Excellent - dark green
  if (score >= 60) return '#32a032' // AGC: Good - green
  if (score >= 40) return '#d4d464' // AGC: Fair - yellow-green
  if (score >= 20) return '#c8a064' // AGC: Poor - orange-brown
  return '#8b5a2b' // AGC: Critical - brown
}

/**
 * AGC: Get background color for grade badge
 */
function getGradeBgColor(grade: string): string {
  switch (grade) {
    case 'excellent': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
    case 'good': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
    case 'fair': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
    case 'poor': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
    case 'critical': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
    default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
  }
}

export const HealthScoreWidget: React.FC<HealthScoreWidgetProps> = ({
  health,
  trend,
  lastUpdated,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  // AGC: No data state
  if (!health && !isLoading) {
    return (
      <div className="flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <div className="text-center">
          <Leaf className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('fields.monitoring.noHealthData')}
          </p>
        </div>
      </div>
    )
  }

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="space-y-2">
            <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    )
  }

  const score = health?.health?.score || 0
  const grade = health?.health?.grade || 'unknown'
  // AGC: [#10] The backend sends gradeLabel in English ("Poor"/"Fair"...). Translate by grade CODE via
  // AGC: the existing i18n keys; fall back to the backend label only for unknown codes. (Translation
  // AGC: layer over an API-injected English string.)
  const GRADE_KEYS = ['excellent', 'good', 'fair', 'poor', 'critical']
  const gradeLabel = GRADE_KEYS.includes(grade)
    ? t(`fields.monitoring.${grade}`)
    : (health?.health?.gradeLabel || t('common.unknown'))
  const scoreColor = health?.health?.gradeColor || getScoreColor(score)
  const zones = health?.zones

  // AGC: Trend indicator
  const getTrendIcon = () => {
    if (!trend) return <Minus className="w-4 h-4 text-gray-400" />
    
    switch (trend.direction) {
      case 'increasing':
        return <TrendingUp className="w-4 h-4 text-green-500" />
      case 'decreasing':
        return <TrendingDown className="w-4 h-4 text-red-500" />
      default:
        return <Minus className="w-4 h-4 text-gray-400" />
    }
  }

  // AGC: Format last updated
  const formatLastUpdated = () => {
    if (!lastUpdated) return null
    const date = new Date(lastUpdated)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    
    if (diffMins < 5) return t('fields.monitoring.justNow')
    if (diffMins < 60) return t('fields.monitoring.minutesAgo', { count: diffMins })
    if (diffHours < 24) return t('fields.monitoring.hoursAgo', { count: diffHours })
    return date.toLocaleDateString()
  }

  // AGC: Calculate stroke dasharray for circular progress
  const radius = 36
  const circumference = 2 * Math.PI * radius
  const progress = (score / 100) * circumference
  const dashOffset = circumference - progress

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-4">
        {/* AGC: Circular Score Indicator */}
        <div className="relative flex-shrink-0">
          <svg width="88" height="88" viewBox="0 0 88 88" className="transform -rotate-90">
            {/* AGC: Background circle */}
            <circle
              cx="44"
              cy="44"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              className="text-gray-200 dark:text-gray-700"
            />
            {/* AGC: Progress circle */}
            <circle
              cx="44"
              cy="44"
              r={radius}
              fill="none"
              stroke={scoreColor}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="transition-all duration-500"
            />
          </svg>
          {/* AGC: Score text in center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">{score}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">/100</span>
            </div>
          </div>
        </div>

        {/* AGC: Health Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getGradeBgColor(grade)}`}>
              {gradeLabel}
            </span>
            {getTrendIcon()}
          </div>
          
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('fields.monitoring.healthScore')}
          </h3>

          {/* AGC: NDVI Value */}
          {health?.health?.metrics?.meanNdvi !== undefined && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              NDVI: {health.health.metrics.meanNdvi.toFixed(2)}
            </p>
          )}

          {/* AGC: Last Updated */}
          {lastUpdated && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {formatLastUpdated()}
            </p>
          )}
        </div>
      </div>

      {/* AGC: Zone Distribution Bar */}
      {zones && zones.totalPixels > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>{t('fields.monitoring.zoneDistribution')}</span>
            <span>{zones.healthyPercentage.toFixed(0)}% {t('fields.monitoring.healthy')}</span>
          </div>
          <div className="h-2 flex rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
            {zones.distribution.excellent > 0 && (
              <div
                className="bg-green-600"
                style={{ width: `${zones.distribution.excellent}%` }}
                title={`${t('fields.monitoring.excellent')}: ${zones.distribution.excellent.toFixed(1)}%`}
              />
            )}
            {zones.distribution.good > 0 && (
              <div
                className="bg-emerald-500"
                style={{ width: `${zones.distribution.good}%` }}
                title={`${t('fields.monitoring.good')}: ${zones.distribution.good.toFixed(1)}%`}
              />
            )}
            {zones.distribution.fair > 0 && (
              <div
                className="bg-yellow-400"
                style={{ width: `${zones.distribution.fair}%` }}
                title={`${t('fields.monitoring.fair')}: ${zones.distribution.fair.toFixed(1)}%`}
              />
            )}
            {zones.distribution.poor > 0 && (
              <div
                className="bg-orange-500"
                style={{ width: `${zones.distribution.poor}%` }}
                title={`${t('fields.monitoring.poor')}: ${zones.distribution.poor.toFixed(1)}%`}
              />
            )}
            {zones.distribution.critical > 0 && (
              <div
                className="bg-red-600"
                style={{ width: `${zones.distribution.critical}%` }}
                title={`${t('fields.monitoring.critical')}: ${zones.distribution.critical.toFixed(1)}%`}
              />
            )}
          </div>
        </div>
      )}

      {/* AGC: Stress Warning */}
      {zones && zones.stressPercentage > 20 && (
        <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {t('fields.monitoring.stressWarning', { percent: zones.stressPercentage.toFixed(0) })}
          </p>
        </div>
      )}
    </div>
  )
}

export default HealthScoreWidget

